import React from "react";
import Navbar from "./components/Navbar";
import { Route, Routes } from "react-router-dom";
import { AuthContextProvider } from "./context/AuthContext";
import { DarkModeProvider } from "./context/DarkModeContext";
import { AnalyticsContextProvider } from "./context/AnalyticsContext";
import ProtectedRoute from "./components/ProtectedRoute";
import SessionTimeout from "./components/SessionTimeout";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import { usePageTracking } from "./hooks/usePageTracking";
import { useAnalytics } from "./context/AnalyticsContext";
import { Box } from "@mui/material";

import FocusTimer from "./components/FocusTimer";

const Addquestions = React.lazy(() => import("./pages/Addquestions"));
const MyLists = React.lazy(() => import("./pages/Mylists"));
const Signin = React.lazy(() => import("./pages/Login"));
const Signup = React.lazy(() => import("./pages/Signup"));
const QuestionsBlitz = React.lazy(() => import("./pages/QuestionsBlitz"));
const QuestionsTest = React.lazy(() => import("./pages/QuestionsTest"));
const Updatequestions = React.lazy(() => import("./pages/Updatequestions"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Leaderboard = React.lazy(() => import("./pages/Leaderboard"));
const GenerateQuiz = React.lazy(() => import("./pages/GenerateQuiz"));
const Settings = React.lazy(() => import("./pages/Settings"));
const QuestionsMatch = React.lazy(() => import("./pages/QuestionsMatch"));
const QuestionsLearn = React.lazy(() => import("./pages/QuestionsLearn"));
const Profile = React.lazy(() => import("./pages/Profile"));
const Shop = React.lazy(() => import("./pages/Shop"));
const DeepTraining = React.lazy(() => import("./pages/DeepTraining"));
const SmartNotes = React.lazy(() => import("./pages/SmartNotes"));
const PremiumAIStudio = React.lazy(() => import("./pages/PremiumAIStudio"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const AdminStatistics = React.lazy(() => import("./pages/AdminStatistics"));
const AdminLogs = React.lazy(() => import("./pages/AdminLogs"));
const AdminTrainingPatterns = React.lazy(() => import("./pages/AdminTrainingPatterns"));
const ChallengeJoin = React.lazy(() => import("./pages/ChallengeJoin"));
const Documentation = React.lazy(() => import("./pages/Documentation"));

// Component to enable page tracking inside the router
function PageTracker() {
	usePageTracking();
	return null;
}

// Wrapper to use analytics context for CookieConsent
function AnalyticsWrapper({ children }) {
	const { handleConsentChange } = useAnalytics();
	return (
		<>
			{children}
			<CookieConsent onConsentChange={handleConsentChange} />
		</>
	);
}

function RouteFallback() {
	return (
		<Box
			sx={{
				display: "grid",
				minHeight: 240,
				placeItems: "center",
				color: "text.secondary",
				fontWeight: 700,
			}}
		>
			QuizBoss laden...
		</Box>
	);
}

function App() {
	const [isZenMode, setIsZenMode] = React.useState(false); // Zen Mode state

	return (
		<AuthContextProvider>
			<DarkModeProvider>
				<AnalyticsContextProvider>
					<AnalyticsWrapper>
						<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
							{!isZenMode && <Navbar />}
							<PageTracker />
							<Box component="main" sx={{ flexGrow: 1 }}>
								<React.Suspense fallback={<RouteFallback />}>
									<Routes>
										<Route
											path="/create-list"
											element={
												<ProtectedRoute>
													<Addquestions />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/generate-quiz"
											element={
												<ProtectedRoute>
													<GenerateQuiz />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/overview"
											element={
												<ProtectedRoute>
													<MyLists />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/"
											element={
												<ProtectedRoute>
													<MyLists />
												</ProtectedRoute>
											}
										/>
										<Route path="/demo" element={<QuestionsBlitz />} />
										<Route
											path="/test"
											element={
												<ProtectedRoute>
													<QuestionsTest />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/match"
											element={
												<ProtectedRoute>
													<QuestionsMatch />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/learn"
											element={
												<ProtectedRoute>
													<QuestionsLearn />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/notes"
											element={
												<ProtectedRoute>
													<SmartNotes />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/premium-ai"
											element={
												<ProtectedRoute>
													<PremiumAIStudio />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/admin"
											element={
												<ProtectedRoute>
													<AdminDashboard />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/admin/statistics"
											element={
												<ProtectedRoute>
													<AdminStatistics />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/admin/logs"
											element={
												<ProtectedRoute>
													<AdminLogs />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/admin/training-patterns"
											element={
												<ProtectedRoute>
													<AdminTrainingPatterns />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/dashboard"
											element={
												<ProtectedRoute>
													<Dashboard />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/leaderboard"
											element={
												<ProtectedRoute>
													<Leaderboard />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/profile"
											element={
												<ProtectedRoute>
													<Profile />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/settings"
											element={
												<ProtectedRoute>
													<Settings />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/shop"
											element={
												<ProtectedRoute>
													<Shop />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/deep-training"
											element={
												<ProtectedRoute>
													<DeepTraining />
												</ProtectedRoute>
											}
										/>
										<Route path="/signin" element={<Signin />} />
										<Route path="/signup" element={<Signup />} />
										<Route
											path="/update"
											element={
												<ProtectedRoute>
													<Updatequestions />
												</ProtectedRoute>
											}
										/>
										<Route path="/challenge/:id" element={<ChallengeJoin />} />
										<Route
											path="/docs"
											element={
												<ProtectedRoute>
													<Documentation />
												</ProtectedRoute>
											}
										/>
									</Routes>
								</React.Suspense>
							</Box>
							<SessionTimeout />
							<Footer />
							<FocusTimer isZenMode={isZenMode} onZenToggle={setIsZenMode} />
						</Box>
					</AnalyticsWrapper>
				</AnalyticsContextProvider>
			</DarkModeProvider>
		</AuthContextProvider>
	);
}

export default App;
